/**
 * PURCHASE INTENT — "remember what the customer was trying to buy".
 *
 * THE PROBLEM (reported 2026-09-30): a new customer clicks "Buy this lead",
 * is sent to Sign in, has no account, clicks "Sign up" — and that link
 * dropped the `next` parameter. After registering they landed on the
 * dashboard with no idea how to get back to the lead they wanted. The same
 * happened for memberships and after the email-verification link.
 *
 * THE FIX: the intent travels TWO ways at once, so either one is enough:
 *   1. `?next=` on every auth link (login <-> register keep passing it on), and
 *   2. a copy in localStorage, which survives a new tab (email link), a
 *      reload, or a customer who wanders off. The ResumePurchaseBanner reads
 *      it and offers a single "Continue" button from any page.
 *
 * The intent is only a navigation hint. It never grants, prices or unlocks
 * anything — every purchase is still decided server-side.
 */

/**
 * `trade` (lead intents only) is the lead's trade slug. Sign-up pre-ticks it
 * so a new buyer does not have to hunt through 50 trade chips for the one
 * they were obviously interested in. Purely a form default.
 */
export type PurchaseIntent =
  | { kind: 'lead'; slug: string; returnTo: string; label?: string; trade?: string; at: number }
  | { kind: 'plan'; plan: string; returnTo: string; label?: string; at: number };

const KEY = 'gg_purchase_intent';
/** Long enough to cover "check your email tomorrow morning". */
const TTL_MS = 48 * 60 * 60 * 1000;

type NewIntent =
  | { kind: 'lead'; slug: string; returnTo: string; label?: string; trade?: string }
  | { kind: 'plan'; plan: string; returnTo: string; label?: string };

export const savePurchaseIntent = (intent: NewIntent): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...intent, at: Date.now() }));
    window.dispatchEvent(new Event('gg-intent'));
  } catch {
    /* private mode — the ?next= parameter still carries the intent */
  }
};

export const readPurchaseIntent = (): PurchaseIntent | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as PurchaseIntent;
    if (!v || !v.returnTo || !v.at || Date.now() - v.at > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (!isSafeNext(v.returnTo)) return null;
    return v;
  } catch {
    return null;
  }
};

export const clearPurchaseIntent = (): void => {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new Event('gg-intent'));
  } catch {
    /* ignore */
  }
};

/** Only same-site paths — blocks `?next=https://evil.example` style redirects. */
export const isSafeNext = (next?: string | null): next is string =>
  !!next && next.startsWith('/') && !next.startsWith('//') && !next.includes('\\');

/** Sanitised `next`, or the fallback when absent / unsafe. */
export const safeNext = (next: string | null | undefined, fallback: string): string =>
  isSafeNext(next) ? next : fallback;

/** Where a lead buyer is sent back to: the SAME lead, with checkout auto-opening. */
export const leadReturnPath = (leadPath: string): string => `${leadPath}?buy=1`;

/** Where a membership buyer is sent back to: the SAME plan's checkout. */
export const planReturnPath = (pricingPath: string, plan: string, coupon?: string): string =>
  `${pricingPath}?plan=${encodeURIComponent(plan)}${coupon ? `&coupon=${encodeURIComponent(coupon)}` : ''}`;

/**
 * Build a Sign-up / Sign-in link that carries the intent forward.
 * `buy=lead|plan` tells the auth pages to show the "you're one step away"
 * banner and to offer contractor account types only.
 */
export const authLink = (authPath: string, next: string, buy?: 'lead' | 'plan', extra = ''): string => {
  const q = new URLSearchParams();
  if (isSafeNext(next)) q.set('next', next);
  if (buy) q.set('buy', buy);
  const s = q.toString();
  return `${authPath}${s ? `?${s}` : ''}${extra ? `${s ? '&' : '?'}${extra}` : ''}`;
};

/** Pathname + search of the current page, for comparing against returnTo. */
export const samePage = (returnTo: string, pathname: string): boolean =>
  returnTo.split('?')[0] === pathname;

/**
 * LANGUAGE-SAFE RETURN TRIP.  (Found in the 2026-09-30 journey review.)
 *
 * The saved return path carries the language it was created in, e.g.
 * `/en/jobs/roofing-fontana?buy=1`. A buyer who switched to Español on the
 * sign-up page was sent back to that ENGLISH url — and because the language
 * follows the URL prefix, the whole site flipped back to English right at the
 * payment step. Worse, `path()` treats the last segment literally, so a plan
 * link like `/en/pricing?plan=pro` could become `/es/pricing?plan=pro`, a page
 * that does not exist.
 *
 * `localizeNext` splits off the query string, re-localises ONLY the path with
 * the app's own `path()` builder (`/es/precios`, `/es/trabajos/...`), and
 * glues the query back on. Unsafe targets are refused exactly like safeNext.
 */
export const localizeNext = (
  next: string | null | undefined,
  pathFn: (p: string) => string,
  fallback: string,
): string => {
  if (!isSafeNext(next)) return fallback;
  const i = next.indexOf('?');
  const base = i >= 0 ? next.slice(0, i) : next;
  const query = i >= 0 ? next.slice(i) : '';
  try {
    return `${pathFn(base)}${query}`;
  } catch {
    return next;
  }
};

